import React, {useEffect, useState, useRef} from 'react';
import styled from 'styled-components';
import {fontSize, wh, fadeInAnimation} from '../../mixins';
import {useDispatch, useSelector} from 'react-redux';
import {setError} from '../error/errorSlice';
import {Amex, MasterCard, Visa} from '../paywall/icons';
import {setCardholderName, setFormValidity} from '../paywall/paywallSlice';
import useDebounce from '../../common/helper';
import {sendAnalyticsEvents} from '../../analytics/common';
import {EVENTS} from '../../analytics/events';
import {selectEventsData, setEventsData} from '../onboarding/onboardingSlice';



const StyledWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 10;
  margin: 0;
  ${fadeInAnimation};

  
  & > * {
   position: relative;
   z-index: 10;
  }
  
    &::before {
    content: "";
    position: absolute;
    z-index: -5;
    top: -25px;
    left: 0;
    width: 100%;
    height: 40px;
    background-image: url('../images/card-bg.png');
    background-size: 100%;
    background-repeat: no-repeat;
    background-position: center;
  }
`;

const StyledCard = styled.div`
  width: 100%;
  min-height: 222px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border-radius: 20px;
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  background-color: #fff;
  z-index: 10;
  
  & > * {
   position: relative;
   z-index: 10;
  }
  
`;

const StyledFieldWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const StyledRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 80%;
`;

const StyledLabel = styled.span`
  color: #323232;
  ${fontSize(14)};
  line-height: 18px;
  text-align: left;
  font-weight: 300;
  padding: 7px 0;
`;

const StyledInput = styled.input`
  ${wh('100%', '28px')};
  color: #00A08B;
  padding: 3px 8px 3px 0;
  background-color: transparent;
  border: none;
  ${fontSize(18)};
  line-height: 22px;
  font-weight: 300;
  
  &::-webkit-credit-card-auto-fill-button {
      visibility: hidden;
      display: none !important;
      pointer-events: none;
      position: absolute;
      right: 0;
  }
  

  &::placeholder {
    color: #ADADAD;
    letter-spacing: 1.2px;
    font-weight: 300;
    ${fontSize(18)};
    line-height: 22px;
  }
  
  &:focus,
  &:hover,
  &:active {
    outline: none;
  }
`;

const StyledBrandIcon = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;
  width: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  
  svg {
      width: 100%;
      height: auto;
  }
`;


const Stripe = ({card, cvc, expiration}) => {
    const [cardBrand, setCardBrand] = useState('');
    const [name, setName] = useState('');
    const [isCvv, setIsCvv] = useState(null);
    const [isExp, setIsExp] = useState(null);
    const [isCard, setIsCard] = useState(null);
    const amplitudeEventData = useSelector(selectEventsData);
    const [err, setErr] = useState('');
    const [isValid, setIsValid] = useState(false);
    const [eventSent, setEventSent] = useState(false);
    const dispatch = useDispatch();

    const debouncedName = useDebounce(name, 500);
    const debouncedErr = useDebounce(err, 500);
    const debouncedValidity = useDebounce(isValid, 500);

    const inputRef = useRef(null);

    card.on('ready', () => {
        document.querySelectorAll('.__PrivateStripeElement-input').forEach(el => {
            el.setAttribute('inputmode', 'numeric');
        });
        card.focus();
    });

    card.on('change', event => {
        if (event.brand && (event.brand !== 'unknown') && (event.brand !== cardBrand)) {
            setCardBrand(event.brand);
        }
        event.complete && expiration.focus();
        fieldEventHandler(event, setIsCard);
    });

    expiration.on('change', event => {
        event.complete && cvc.focus();
        fieldEventHandler(event, setIsExp);
    });

    cvc.on('change', event => {
        event.complete && inputRef?.current?.focus();
        fieldEventHandler(event, setIsCvv);
    });

    useEffect(() => {
        card.mount('#card-number-element');
        expiration.mount('#card-expiry-element');
        cvc.mount('#card-cvc-element');
    }, [card, cvc, expiration]);


    const fieldEventHandler = (evt, setValidity) => {
        if (evt.error) {
            setErr(evt.error.message);
        }
        setValidity(evt.complete);
    };

    useEffect(() => {
        if (cardBrand && amplitudeEventData && (amplitudeEventData['card_brand'] !== cardBrand) && !eventSent) {
            dispatch(setEventsData({id: 'card_brand', data: cardBrand}));
            sendAnalyticsEvents(EVENTS.cardMethodChosen, {...amplitudeEventData, 'card_brand': cardBrand}); //Analytics event for starting typing the card number
            setEventSent(true);
        }
    }, [cardBrand, amplitudeEventData, dispatch]);

    useEffect(() => {
        if (isCard && isCvv && isExp) {
            setIsValid(true);
        } else {
            setIsValid(false);
        }

    }, [isCard, isCvv, isExp, dispatch]);


    useEffect(() => {
        if (debouncedName) {
            dispatch(setCardholderName(debouncedName));
        }

    }, [debouncedName, dispatch]);

    useEffect(() => {
        if (debouncedErr) {
            dispatch(setError({show: true, type: 'cardValidation', text: debouncedErr}));
        }
    }, [debouncedErr, dispatch]);

    useEffect(() => {
        dispatch(setFormValidity(debouncedValidity));
    }, [debouncedValidity, dispatch]);


    let brandIcon;

    switch (cardBrand) {
    case 'visa':
        brandIcon = Visa;
        break;
    case 'mastercard':
        brandIcon = MasterCard;
        break;
    case 'amex':
        brandIcon = Amex;
        break;
    default:
        brandIcon = null;
    }

    return (
        <StyledWrapper>
            <StyledCard>
                <StyledFieldWrapper>
                    <StyledLabel>Card number</StyledLabel>
                    <div id={'card-number-element'} />
                </StyledFieldWrapper>

                <StyledRow>
                    <StyledFieldWrapper>
                        <StyledLabel>MM/YY</StyledLabel>
                        <div id={'card-expiry-element'} />
                    </StyledFieldWrapper>


                    <StyledFieldWrapper>
                        <StyledLabel>CVC2</StyledLabel>
                        <div id={'card-cvc-element'} />
                    </StyledFieldWrapper>
                </StyledRow>

                <StyledFieldWrapper>
                    <StyledLabel>Cardholder Name</StyledLabel>
                    <StyledInput ref={inputRef} onChange={e => setName(e.target.value)} type='text' autocomplete='cc-name' placeholder='Name' value={name}/>
                </StyledFieldWrapper>

                <StyledBrandIcon>{brandIcon}</StyledBrandIcon>
            </StyledCard>
        </StyledWrapper>
    );
};


export default Stripe;